import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getMemberApplications, getMemberNotifications, markNotificationRead } from "../firebase/services";
import { sendEmailVerification } from "firebase/auth";
import { auth } from "../firebase/config";

const NAV_ITEMS = [
  { icon: "⊞", label: "Dashboard",          id: "overview" },
  { icon: "👤", label: "My Profile",         id: "profile" },
  { icon: "🪪", label: "Membership",         id: "membership" },
  { icon: "📋", label: "Grant Applications", id: "applications" },
  { icon: "💰", label: "My Balances",        id: "balances" },
  { icon: "💸", label: "Funding Updates",    id: "funding" },
  { icon: "🔔", label: "Notifications",      id: "notifications" },
  { icon: "⚙️", label: "Settings",           id: "settings" },
];

function StatusBadge({ status }) {
  const map = {
    "Under Review": ["badge-blue",  "🔄"],
    "Approved":     ["badge-green", "✓"],
    "Disbursed":    ["badge-gold",  "💸"],
    "Rejected":     ["badge-red",   "✗"],
  };
  const [cls, icon] = map[status] || ["badge-blue", "?"];
  return <span className={`badge ${cls}`}>{icon} {status}</span>;
}

function MembershipGate() {
  return (
    <div style={{textAlign:"center",padding:"60px 20px",maxWidth:520,margin:"0 auto"}}>
      <div style={{width:80,height:80,borderRadius:"50%",background:"rgba(201,150,62,0.08)",border:"2px solid rgba(201,150,62,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,margin:"0 auto 24px"}}>🔒</div>
      <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:26,color:"var(--navy)",marginBottom:12}}>Membership Required</h3>
      <p style={{color:"var(--text-muted)",fontSize:15,lineHeight:1.75,marginBottom:28}}>Only paid members can apply for grants. Complete your ₦250 membership payment to unlock all grant programs.</p>
      <Link to="/membership" className="btn-primary" style={{display:"inline-block"}}>Pay ₦250 & Unlock Grants →</Link>
    </div>
  );
}

export default function DashboardPage() {
  const [active, setActive]             = useState("overview");
  const [mobileOpen, setMobileOpen]      = useState(false);
  const [applications, setApplications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loadingData, setLoadingData]   = useState(true);
  const [verifyMsg, setVerifyMsg]       = useState("");
  const [verifySent, setVerifySent]     = useState(false);

  const { currentUser, userProfile, logout, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const profile      = userProfile || {};
  const grantBalance = applications.filter(a => a.status === "Disbursed").reduce((s, a) => s + (a.disbursedAmount || Number(a.fundingAmount) || 0), 0);
  const pendingGrant = applications.filter(a => a.status === "Approved").reduce((s, a) => s + (Number(a.fundingAmount) || 0), 0);
  const reviewGrant  = applications.filter(a => a.status === "Under Review").reduce((s, a) => s + (Number(a.fundingAmount) || 0), 0);
  const unreadCount  = notifications.filter(n => n.unread).length;
  const isVerified   = currentUser?.emailVerified;
  const isAdmin = currentUser?.email === "doctor002x@gmail.com" || userProfile?.role === "admin";

  useEffect(() => {
    if (!currentUser) { navigate("/login"); return; }
    const load = async () => {
      try {
        const [apps, notifs] = await Promise.all([
          getMemberApplications(currentUser.uid),
          getMemberNotifications(currentUser.uid),
        ]);
        setApplications(apps);
        setNotifications(notifs);
      } catch (e) { console.error(e); }
      finally { setLoadingData(false); }
    };
    load();
  }, [currentUser, navigate]);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const handleResendVerification = async () => {
    try {
      await sendEmailVerification(auth.currentUser);
      setVerifyMsg("✅ Verification email sent! Check your inbox.");
      setVerifySent(true);
    } catch (err) {
      setVerifyMsg("Failed to send. Please wait a moment and try again.");
    }
  };

  const handleMarkRead = async (notifId) => {
    await markNotificationRead(currentUser.uid, notifId);
    setNotifications(ns => ns.map(n => n.id === notifId ? {...n, unread: false} : n));
  };

  if (loadingData) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#F0EDE8"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:32,marginBottom:16,animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</div>
        <div style={{color:"var(--text-muted)",fontSize:15}}>Loading your dashboard...</div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (active) {
      case "overview":      return <OverviewPanel />;
      case "profile":       return <ProfilePanel />;
      case "membership":    return <MembershipPanel />;
      case "applications":  return <ApplicationsPanel />;
      case "balances":      return <BalancesPanel />;
      case "funding":       return <FundingPanel />;
      case "notifications": return <NotificationsPanel />;
      case "settings":      return <SettingsPanel />;
      default: return (
        <div style={{textAlign:"center",padding:"80px",color:"var(--text-muted)"}}>
          <div style={{fontSize:48,marginBottom:16}}>🚧</div>
          <div style={{fontSize:15}}>Coming soon</div>
        </div>
      );
    }
  };

  return (
    <>
      <style>{`
        .dash-wrap { display:flex; min-height:100vh; background:#F0EDE8; }
        .sidebar { width:256px; background:#0B1F3A; flex-shrink:0; display:flex; flex-direction:column; position:sticky; top:0; height:100vh; overflow-y:auto; transition: transform 0.3s; }
        .sidebar.mobile-open { transform: translateX(0); }
        .sidebar-logo { padding:20px 18px; border-bottom:1px solid rgba(255,255,255,0.06); display: flex; align-items:center; justify-content: space-between; }
        .nav-item { display:flex; align-items:center; gap:11px; padding:11px 18px; cursor:pointer; transition:all 0.2s; color:rgba(255,255,255,0.55); font-size:13px; position:relative; }
        .nav-item:hover { color:white; background:rgba(255,255,255,0.05); }
        .nav-item.active { color:white; background:rgba(201,150,62,0.12); }
        .nav-item.active::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:#C9963E; border-radius:0 2px 2px 0; }
        .sidebar-section { font-size:9px; color:rgba(255,255,255,0.25); letter-spacing:2px; text-transform:uppercase; padding:12px 18px 5px; }
        .dash-main { flex:1; overflow-y:auto; min-width:0; }
        .verify-banner { background:linear-gradient(90deg,#7c3aed,#6d28d9); color:white; padding:12px 24px; display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
        .dash-topbar { background:white; padding:14px 26px; display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid var(--border-light); position:sticky; top:0; z-index:10; flex-wrap:wrap; gap:10px; }
        .topbar-title { font-family:'Playfair Display',serif; font-size:19px; font-weight:700; color:var(--navy); }
        .dash-content { padding:22px 26px; }
        .balances-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:18px; }
        .stat-cards { display:grid; grid-template-columns:repeat(5,1fr); gap:14px; margin-bottom:18px; }
        .dash-stat { background:white; border:1px solid var(--border-light); padding:18px; }

        .dash-stat-val { font-family:'Playfair Display',serif; font-size:20px; font-weight:700; color:var(--navy); }
        .dash-stat-label { font-size:10px; color:var(--text-muted); margin-top:3px; text-transform:uppercase; letter-spacing:0.5px; }
        .dash-grid { display:grid; grid-template-columns:3fr 2fr; gap:16px; }
        .dash-card { background:white; border:1px solid var(--border-light); padding:20px; }
        .dash-card-title { font-family:'Playfair Display',serif; font-size:16px; font-weight:700; color:var(--navy); margin-bottom:14px; display:flex; justify-content:space-between; align-items:center; }
        .app-row { padding:13px 0; border-bottom:1px solid var(--border-light); display:flex; gap:12px; align-items:flex-start; }
        .app-row:last-child { border-bottom:none; }
        .grant-empty { border:2px dashed rgba(201,150,62,0.15); padding:28px; text-align:center; border-radius:2px; }
        .mem-stat { flex:1; background:rgba(255,255,255,0.05); padding:12px; border-radius:2px; text-align:center; }
        .mem-stat-val { font-family:'Playfair Display',serif; font-size:15px; color:#E8B96A; font-weight:700; }
        .mem-stat-label { font-size:10px; color:rgba(255,255,255,0.4); margin-top:3px; text-transform:uppercase; }
        .review-row { display:flex; justify-content:space-between; padding:11px 0; border-bottom:1px solid var(--border-light); }
        @media(max-width:1100px){ .stat-cards{grid-template-columns:repeat(3,1fr);} .dash-grid{grid-template-columns:1fr;} .balances-grid{grid-template-columns:1fr;} }
        @media(max-width:800px){ 
          .sidebar{position:fixed; left:-256px; z-index:100; height:100vh;}
          .sidebar.mobile-open{left:0;}
          .dash-content{padding:14px;}
          .stat-cards{grid-template-columns:1fr 1fr;}
        }
        @media(min-width:801px){ .mobile-toggle { display:none; } }
      `}</style>

      <div className="dash-wrap">
        {/* SIDEBAR */}
        <aside className={`sidebar ${mobileOpen ? "mobile-open" : ""}`}>
          <div className="sidebar-logo">
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:34,height:34,borderRadius:"50%",background:"linear-gradient(135deg,#C9963E,#E8B96A)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Playfair Display',serif",fontWeight:900,color:"#0B1F3A",fontSize:14,flexShrink:0}}>RF</div>
              <div style={{fontFamily:"'Playfair Display',serif",color:"white",fontSize:13,fontWeight:700,lineHeight:1.3}}>
                Revolutionary<br/>Foundation
                <span style={{display:"block",fontSize:9,color:"#E8B96A",letterSpacing:1.5,textTransform:"uppercase",fontFamily:"'DM Sans',sans-serif",fontWeight:400}}>Member Portal</span>
              </div>
            </div>
            <button style={{background:'none', border:'none', color:'white', fontSize:20}} onClick={() => setMobileOpen(false)}>×</button>
          </div>
          <nav style={{padding:"12px 0",flex:1}}>
            <div className="sidebar-section">Main</div>
            {NAV_ITEMS.slice(0,5).map(item=>(
              <div key={item.id} className={`nav-item${active===item.id?" active":""}`} onClick={()=>{setActive(item.id); setMobileOpen(false);}}>
                <span style={{fontSize:15,width:18,textAlign:"center"}}>{item.icon}</span>
                {item.label}
                {item.id==="notifications"&&unreadCount>0&&(
                  <span style={{marginLeft:"auto",background:"#ef4444",color:"white",fontSize:10,fontWeight:700,borderRadius:"50%",width:17,height:17,display:"flex",alignItems:"center",justifyContent:"center"}}>{unreadCount}</span>
                )}
              </div>
            ))}
            {isAdmin && (
              <Link to="/admin" className="nav-item" style={{color: "#E8B96A"}}>
                <span style={{fontSize:15,width:18,textAlign:"center"}}>⚙️</span>
                Admin Panel
              </Link>
            )}
            <div className="sidebar-section" style={{marginTop:8}}>Foundation</div>
            {NAV_ITEMS.slice(5).map(item=>(
              <div key={item.id} className={`nav-item${active===item.id?" active":""}`} onClick={()=>{setActive(item.id); setMobileOpen(false);}}>
                <span style={{fontSize:15,width:18,textAlign:"center"}}>{item.icon}</span>
                {item.label}
              </div>
            ))}
          </nav>
          <div style={{padding:"14px 18px",borderTop:"1px solid rgba(255,255,255,0.06)"}}>
            <div style={{display:"flex",alignItems:"center",gap:9}}>
              <div style={{width:32,height:32,borderRadius:"50%",background:"linear-gradient(135deg,#C9963E,#E8B96A)",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,color:"#0B1F3A",fontSize:12,flexShrink:0}}>
                {profile.name?.split(" ").map(n=>n[0]).join("").slice(0,2)||"RF"}
              </div>
              <div>
                <div style={{fontSize:12,color:"white",fontWeight:600}}>{profile.name||"Member"}</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>{currentUser?.email}</div>
              </div>
            </div>
            <div style={{marginTop:10,display:"flex",alignItems:"center",gap:7,background:profile.hasPaidMembership?"rgba(34,197,94,0.1)":"rgba(239,68,68,0.1)",border:`1px solid ${profile.hasPaidMembership?"rgba(34,197,94,0.2)":"rgba(239,68,68,0.2)"}`,padding:"5px 10px",borderRadius:4}}>
              <span style={{fontSize:13}}>{profile.hasPaidMembership?"✅":"❌"}</span>
              <span style={{fontSize:11,color:profile.hasPaidMembership?"#4ade80":"#f87171",fontWeight:600}}>
                {profile.hasPaidMembership?"Membership Active":"Membership Unpaid"}
              </span>
            </div>
            <button onClick={handleLogout} style={{display:"block",width:"100%",marginTop:10,fontSize:12,color:"rgba(255,255,255,0.3)",textAlign:"center",background:"none",border:"none",cursor:"pointer"}}>Sign Out</button>
            <Link to="/" style={{display:"block",fontSize:11,color:"rgba(255,255,255,0.2)",textAlign:"center",marginTop:4}}>← Back to Website</Link>
          </div>
        </aside>

        {/* MAIN */}
        <main className="dash-main">
          {/* EMAIL VERIFICATION BANNER */}
          {!isVerified && (
            <div className="verify-banner">
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:18}}>📧</span>
                <div>
                  <div style={{fontSize:14,fontWeight:600}}>Please verify your email address</div>
                  <div style={{fontSize:12,opacity:0.8}}>Check your inbox for a verification link from Revolutionary Foundation</div>
                </div>
              </div>
              <div style={{display:"flex",gap:10,alignItems:"center",flexShrink:0}}>
                {verifyMsg && <span style={{fontSize:12,opacity:0.9}}>{verifyMsg}</span>}
                {!verifySent && (
                  <button onClick={handleResendVerification} style={{background:"rgba(255,255,255,0.2)",color:"white",border:"1px solid rgba(255,255,255,0.3)",padding:"6px 14px",borderRadius:4,fontSize:12,fontWeight:600,cursor:"pointer"}}>
                    Resend Email
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="dash-topbar">
            <div style={{display:'flex', alignItems:'center', gap:12}}>
              <button className="mobile-toggle" style={{background:'none', border:'none', fontSize:20}} onClick={() => setMobileOpen(true)}>☰</button>
              <div className="topbar-title">{NAV_ITEMS.find(n=>n.id===active)?.label||"Dashboard"}</div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              {profile.hasPaidMembership
                ?<Link to="/apply" className="btn-primary" style={{fontSize:13,padding:"7px 16px"}}>+ Apply for Grant</Link>
                :<Link to="/membership" style={{fontSize:13,padding:"7px 16px",background:"rgba(239,68,68,0.08)",color:"#dc2626",border:"1px solid rgba(239,68,68,0.2)",borderRadius:4}}>🔒 Pay Membership to Apply</Link>
              }
              <div style={{position:"relative",fontSize:19,cursor:"pointer"}} onClick={()=>setActive("notifications")}>
                🔔
                {unreadCount>0&&<div style={{position:"absolute",top:-2,right:-2,width:8,height:8,background:"#ef4444",borderRadius:"50%",border:"2px solid white"}}/>}
              </div>
            </div>
          </div>
          <div className="dash-content">{renderContent()}</div>
        </main>
      </div>
    </>
  );

  // ── OVERVIEW ──────────────────────────────────────────────
  function OverviewPanel() {
    const initials = profile.name?.split(" ").map(n=>n[0]).join("").slice(0,2)||"RF";
    return (
      <>
        {/* WELCOME */}
        <div style={{background:"linear-gradient(135deg,#0B1F3A,#1a3a5c)",padding:"20px 24px",marginBottom:16,display:"flex",gap:16,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{width:48,height:48,borderRadius:"50%",background:"linear-gradient(135deg,#C9963E,#E8B96A)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:"#0B1F3A",flexShrink:0}}>{initials}</div>
          <div style={{flex:1}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:19,color:"white",fontWeight:700}}>Welcome back, {profile.name?.split(" ")[0]||"Member"} 👋</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginTop:2}}>{profile.org||"Revolutionary Foundation Member"} · {profile.state||""} · {profile.memberId||""}</div>
          </div>
          <div style={{background:profile.hasPaidMembership?"rgba(34,197,94,0.12)":"rgba(239,68,68,0.12)",border:`1px solid ${profile.hasPaidMembership?"rgba(34,197,94,0.3)":"rgba(239,68,68,0.3)"}`,padding:"7px 14px",borderRadius:4,textAlign:"center"}}>
            <div style={{fontSize:10,color:profile.hasPaidMembership?"#4ade80":"#f87171",letterSpacing:1,textTransform:"uppercase"}}>Membership</div>
            <div style={{fontSize:13,color:"white",fontWeight:700,marginTop:1}}>{profile.hasPaidMembership?"✓ Active":"✗ Unpaid"}</div>
          </div>
        </div>

        {/* BALANCE CARDS */}
        <div className="balances-grid">
          <div style={{background:"linear-gradient(135deg,#0B1F3A,#1a3a5c)",padding:"22px",borderRadius:2,position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",top:-12,right:-12,fontSize:64,opacity:0.06}}>🪪</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Membership Balance</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:36,fontWeight:900,color:profile.hasPaidMembership?"#E8B96A":"rgba(255,255,255,0.15)",lineHeight:1}}>
              ₦{profile.hasPaidMembership?"250":"0"}.00
            </div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:8,lineHeight:1.5}}>
              {profile.hasPaidMembership?`✓ Paid · Ref: ${profile.membershipRef||"—"}`:"Not paid. Pay ₦250 to activate."}
            </div>
            {!profile.hasPaidMembership&&<Link to="/membership" className="btn-primary" style={{display:"inline-block",fontSize:12,padding:"7px 14px",marginTop:12}}>Pay Now →</Link>}
          </div>
          <div style={{background:"linear-gradient(135deg,#071e0f,#0d3324)",padding:"22px",borderRadius:2,position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",top:-12,right:-12,fontSize:64,opacity:0.06}}>💰</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>Grant Balance</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:36,fontWeight:900,color:grantBalance>0?"#E8B96A":"rgba(255,255,255,0.15)",lineHeight:1}}>
              ₦{grantBalance>0?grantBalance.toLocaleString():"0"}.00
            </div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:8,lineHeight:1.5}}>
              {grantBalance>0?`Disbursed from ${applications.filter(a=>a.status==="Disbursed").length} grant(s)`:"No grants disbursed yet."}
            </div>
            {pendingGrant>0&&<div style={{fontSize:12,color:"#4ade80",marginTop:6}}>⏳ ₦{pendingGrant.toLocaleString()} pending release</div>}
            {reviewGrant>0&&<div style={{fontSize:12,color:"#3b82f6",marginTop:4}}>🔄 ₦{reviewGrant.toLocaleString()} pending review</div>}
          </div>
        </div>

        {/* STATS */}
        <div className="stat-cards" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
          {[
            [applications.length.toString(),"Applications","Total submitted"],
            [applications.filter(a=>a.status==="Under Review").length.toString(),"Pending Review","Awaiting approval"],
            [applications.filter(a=>a.status==="Approved"||a.status==="Disbursed").length.toString(),"Approved","Grants approved"],
            [`₦${grantBalance.toLocaleString()}`,"Disbursed","Total received"],
            [profile.hasPaidMembership?"Active":"Unpaid","Membership",profile.hasPaidMembership?"₦250 paid":"₦250 required"],
          ].map(([val,label,sub])=>(
            <div className="dash-stat" key={label}>
              <div className="dash-stat-val" style={{color:label==="Membership"?(profile.hasPaidMembership?"#16a34a":"#dc2626"):"var(--navy)"}}>{val}</div>
              <div className="dash-stat-label">{label}</div>
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:3}}>{sub}</div>
            </div>
          ))}
        </div>

        {/* MAIN GRID */}
        <div className="dash-grid">
          <div className="dash-card">
            <div className="dash-card-title">
              My Grant Applications
              <span style={{fontSize:12,color:"#C9963E",cursor:"pointer"}} onClick={()=>setActive("applications")}>View all →</span>
            </div>
            {!profile.hasPaidMembership ? (
              <div className="grant-empty">
                <div style={{fontSize:28,marginBottom:8}}>🔒</div>
                <div style={{fontSize:14,fontWeight:600,color:"var(--navy)",marginBottom:6}}>Grant Applications Locked</div>
                <div style={{fontSize:13,color:"var(--text-muted)",marginBottom:14}}>Pay ₦250 membership to unlock.</div>
                <Link to="/membership" className="btn-primary" style={{fontSize:13,padding:"9px 18px",display:"inline-block"}}>Pay Membership →</Link>
              </div>
            ) : applications.length === 0 ? (
              <div className="grant-empty">
                <div style={{fontSize:28,marginBottom:8}}>📋</div>
                <div style={{fontSize:14,fontWeight:600,color:"var(--navy)",marginBottom:6}}>No Applications Yet</div>
                <div style={{fontSize:13,color:"var(--text-muted)",marginBottom:14}}>Submit your first grant application today.</div>
                <Link to="/apply" className="btn-primary" style={{fontSize:13,padding:"9px 18px",display:"inline-block"}}>Apply Now →</Link>
              </div>
            ) : (
              applications.slice(0,4).map(a=>(
                <div className="app-row" key={a.id}>
                  <div style={{width:34,height:34,borderRadius:4,background:"rgba(201,150,62,0.08)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>📋</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:"var(--navy)",marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.projectTitle}</div>
                    <div style={{fontSize:11,color:"var(--text-muted)"}}>{a.grantType?.split("(")[0]?.trim()} · {a.submittedAt?.toDate?.().toLocaleDateString("en-GB")||"—"}</div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:"#C9963E",marginBottom:3}}>₦{Number(a.fundingAmount).toLocaleString()}</div>
                    <StatusBadge status={a.status}/>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {pendingGrant>0&&(
              <div style={{background:"linear-gradient(135deg,#071e0f,#0d3324)",padding:"18px 20px",borderRadius:2}}>
                <div style={{fontSize:10,color:"#4ade80",letterSpacing:2,textTransform:"uppercase",marginBottom:6}}>⏳ Pending Disbursement</div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:"#E8B96A",fontWeight:700}}>₦{pendingGrant.toLocaleString()}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:4}}>Approved grant awaiting release</div>
              </div>
            )}
            {reviewGrant>0&&(
              <div style={{background:"linear-gradient(135deg,#071428,#122844)",padding:"18px 20px",borderRadius:2,border:"1px solid rgba(37,99,235,0.2)"}}>
                <div style={{fontSize:10,color:"#3b82f6",letterSpacing:2,textTransform:"uppercase",marginBottom:6}}>🔄 Pending Approval</div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:"#E8B96A",fontWeight:700}}>₦{reviewGrant.toLocaleString()}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:4}}>Application under internal review</div>
              </div>
            )}
            <div className="dash-card">
              <div className="dash-card-title">Recent Notifications</div>
              {notifications.slice(0,3).length===0?(
                <div style={{textAlign:"center",padding:"20px",color:"var(--text-muted)",fontSize:13}}>No notifications yet.</div>
              ):notifications.slice(0,3).map((n,i)=>(
                <div key={n.id||i} style={{display:"flex",gap:10,padding:"9px 0",borderBottom:i<2?"1px solid var(--border-light)":"none"}}>
                  <div style={{fontSize:16,flexShrink:0}}>{n.icon}</div>
                  <div>
                    <div style={{fontSize:12,color:"var(--navy)",lineHeight:1.5,fontWeight:n.unread?600:400}}>{n.text}</div>
                    <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>{n.createdAt?.toDate?.().toLocaleDateString()||"Recently"}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{background:"linear-gradient(135deg,#071428,#0B1F3A)",padding:"18px 20px",borderRadius:2}}>
              <div style={{fontSize:10,color:"#E8B96A",letterSpacing:2,textTransform:"uppercase",marginBottom:6}}>Next Grant Cycle</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,color:"white",fontWeight:700,marginBottom:5}}>September 1, 2026</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",lineHeight:1.6,marginBottom:12}}>Impact Partnership Grants — up to ₦50M.</div>
              {profile.hasPaidMembership
                ?<Link to="/apply" className="btn-primary" style={{fontSize:12,padding:"8px 14px",display:"inline-block"}}>Apply Now →</Link>
                :<Link to="/membership" style={{fontSize:12,color:"#E8B96A"}}>Pay membership first →</Link>
              }
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── APPLICATIONS ──────────────────────────────────────────
  function ApplicationsPanel() {
    if (!profile.hasPaidMembership) return <MembershipGate />;
    return (
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
          <div style={{fontSize:14,color:"var(--text-muted)"}}>{applications.length} total application(s)</div>
          <Link to="/apply" className="btn-primary" style={{fontSize:13,padding:"9px 16px"}}>+ New Application</Link>
        </div>
        {applications.length===0?(
          <div className="grant-empty">
            <div style={{fontSize:32,marginBottom:10}}>📋</div>
            <div style={{fontSize:14,fontWeight:600,color:"var(--navy)",marginBottom:6}}>No Applications Yet</div>
            <div style={{fontSize:13,color:"var(--text-muted)",marginBottom:16}}>Submit your first grant application today.</div>
            <Link to="/apply" className="btn-primary" style={{fontSize:13,padding:"9px 18px",display:"inline-block"}}>Apply Now →</Link>
          </div>
        ):applications.map(a=>(
          <div key={a.id} style={{background:"white",border:"1px solid var(--border-light)",padding:"18px 20px",marginBottom:12,borderLeft:`3px solid ${a.status==="Disbursed"?"#C9963E":a.status==="Approved"?"#16a34a":a.status==="Rejected"?"#dc2626":"#2563eb"}`}}>
            <div style={{display:"flex",gap:12,alignItems:"flex-start",flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:200}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,color:"var(--navy)",marginBottom:4}}>{a.projectTitle}</div>
                <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:6}}>{a.category} · {a.state} · Submitted {a.submittedAt?.toDate?.().toLocaleDateString("en-GB")||"—"}</div>
                <div style={{fontSize:12,color:"var(--text-muted)"}}>Grant Type: {a.grantType?.split("(")[0]?.trim()||"—"}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:"#C9963E",marginBottom:6}}>₦{Number(a.fundingAmount).toLocaleString()}</div>
                <StatusBadge status={a.status}/>
                {a.status==="Disbursed"&&<div style={{fontSize:11,color:"#16a34a",marginTop:4}}>✓ Funds released</div>}
                {a.status==="Rejected"&&a.adminNotes&&<div style={{fontSize:11,color:"#dc2626",marginTop:4}}>Reason: {a.adminNotes}</div>}
              </div>
            </div>
            {a.status==="Approved"&&(
              <div style={{marginTop:12,padding:"10px 14px",background:"rgba(34,197,94,0.06)",border:"1px solid rgba(34,197,94,0.15)",borderRadius:4,fontSize:13,color:"#16a34a"}}>
                ✅ Your application is approved! Funds will be disbursed to your account shortly.
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // ── BALANCES ──────────────────────────────────────────────
  function BalancesPanel() {
    return (
      <div style={{maxWidth:760}}>
        <p style={{color:"var(--text-muted)",fontSize:15,marginBottom:24,lineHeight:1.75}}>Your account has two separate balances — your one-time membership fee and any grant funds disbursed to you.</p>
        <div style={{marginBottom:20}}>
          <div style={{fontSize:11,color:"#C9963E",fontWeight:600,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>① Membership Balance</div>
          <div style={{background:"linear-gradient(135deg,#0B1F3A,#1a3a5c)",padding:"28px",borderRadius:2}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:14}}>
              <div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",letterSpacing:1.5,textTransform:"uppercase",marginBottom:8}}>Amount Paid</div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:48,fontWeight:900,color:profile.hasPaidMembership?"#E8B96A":"rgba(255,255,255,0.15)",lineHeight:1}}>
                  ₦{profile.hasPaidMembership?"250":"0"}.00
                </div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",marginTop:10}}>
                  {profile.hasPaidMembership?`Paid on ${profile.membershipDate||"—"}`:"No payment recorded yet"}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <span className={`badge ${profile.hasPaidMembership?"badge-green":"badge-red"}`} style={{fontSize:13,padding:"8px 14px"}}>
                  {profile.hasPaidMembership?"✓ Confirmed":"✗ Unpaid"}
                </span>
                {profile.hasPaidMembership&&<div style={{marginTop:12,fontSize:13,color:"#E8B96A",fontWeight:600}}>Ref: {profile.membershipRef||"—"}</div>}
              </div>
            </div>
            {!profile.hasPaidMembership&&<div style={{marginTop:20,paddingTop:16,borderTop:"1px solid rgba(255,255,255,0.06)"}}><Link to="/membership" className="btn-primary" style={{display:"inline-block"}}>Pay ₦250 Membership →</Link></div>}
          </div>
        </div>
        <div>
          <div style={{fontSize:11,color:"#C9963E",fontWeight:600,letterSpacing:2,textTransform:"uppercase",marginBottom:10}}>② Grant Balance</div>
          <div style={{background:"linear-gradient(135deg,#071e0f,#0d3324)",padding:"28px",borderRadius:2}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:14}}>
              <div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",letterSpacing:1.5,textTransform:"uppercase",marginBottom:8}}>Total Disbursed</div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:48,fontWeight:900,color:grantBalance>0?"#E8B96A":"rgba(255,255,255,0.15)",lineHeight:1}}>
                  ₦{grantBalance>0?grantBalance.toLocaleString():"0"}.00
                </div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",marginTop:10}}>
                  {grantBalance>0?`Across ${applications.filter(a=>a.status==="Disbursed").length} disbursed grant(s)`:"No grants disbursed yet"}
                </div>
              </div>
              {(pendingGrant + reviewGrant) > 0 && (
                <div style={{background:"rgba(255,255,255,0.04)",padding:"14px 18px",borderRadius:2,border:"1px solid rgba(74,222,128,0.15)"}}>
                  <div style={{fontSize:10,color:"#4ade80",letterSpacing:1,textTransform:"uppercase",marginBottom:5}}>AWAITING RELEASE</div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:24,color:"#E8B96A",fontWeight:700}}>₦{(pendingGrant + reviewGrant).toLocaleString()}</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.35)",marginTop:3}}>
                    {pendingGrant > 0 && `${pendingGrant.toLocaleString()} Approved`}
                    {pendingGrant > 0 && reviewGrant > 0 && " · "}
                    {reviewGrant > 0 && `${reviewGrant.toLocaleString()} Pending`}
                  </div>
                </div>
              )}
            </div>
            {applications.filter(a=>a.status==="Disbursed").length>0&&(
              <div style={{marginTop:20,paddingTop:16,borderTop:"1px solid rgba(255,255,255,0.06)"}}>
                {applications.filter(a=>a.status==="Disbursed").map(a=>(
                  <div key={a.id} style={{display:"flex",justifyContent:"space-between",padding:"9px 0",borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                    <div>
                      <div style={{fontSize:13,color:"rgba(255,255,255,0.75)",fontWeight:600}}>{a.projectTitle}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:2}}>{a.disbursedAt?.toDate?.().toLocaleDateString()||"—"}</div>
                    </div>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,color:"#E8B96A",fontWeight:700}}>+₦{(a.disbursedAmount||Number(a.fundingAmount)).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
            {grantBalance===0&&<div style={{marginTop:16,fontSize:13,color:"rgba(255,255,255,0.3)",lineHeight:1.7}}>
              {profile.hasPaidMembership?<>💡 You're eligible to apply. <Link to="/apply" style={{color:"#E8B96A"}}>Apply for a grant →</Link></>:"💡 Pay membership first, then apply for grants."}
            </div>}
          </div>
        </div>
      </div>
    );
  }

  // ── MEMBERSHIP ────────────────────────────────────────────
  function MembershipPanel() {
    const initials = profile.name?.split(" ").map(n=>n[0]).join("").slice(0,2)||"RF";
    return (
      <div style={{maxWidth:620}}>
        <div style={{background:"linear-gradient(135deg,#0B1F3A,#1a3a5c)",padding:"26px 28px",marginBottom:16}}>
          <div style={{width:56,height:56,borderRadius:"50%",background:"linear-gradient(135deg,#C9963E,#E8B96A)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,color:"#0B1F3A",marginBottom:12}}>{initials}</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,color:"white",fontWeight:700}}>{profile.name}</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginTop:3}}>{profile.org||"—"} · {profile.state||"—"}</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap",marginTop:16}}>
            {[["Member ID",profile.memberId||"—"],["Joined",profile.createdAt?.toDate?.().toLocaleDateString()||"—"],["Status",profile.hasPaidMembership?"✓ Active":"✗ Unpaid"]].map(([l,v])=>(
              <div className="mem-stat" key={l}>
                <div className="mem-stat-val" style={{color:l==="Status"?(profile.hasPaidMembership?"#4ade80":"#f87171"):"#E8B96A"}}>{v}</div>
                <div className="mem-stat-label">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="dash-card" style={{marginBottom:14}}>
          <div className="dash-card-title">Membership Payment</div>
          {[["Fee","₦250.00"],["Status",null],...(profile.hasPaidMembership?[["Date Paid",profile.membershipDate||"—"],["Reference",profile.membershipRef||"—"]]:[])]
            .map(([label,val])=>(
            <div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:"1px solid var(--border-light)"}}>
              <span style={{fontSize:14,color:"var(--text-muted)"}}>{label}</span>
              {label==="Status"
                ?<span className={`badge ${profile.hasPaidMembership?"badge-green":"badge-red"}`}>{profile.hasPaidMembership?"✓ Paid":"✗ Unpaid"}</span>
                :<span style={{fontSize:14,fontWeight:700,color:label==="Reference"?"#C9963E":"var(--navy)"}}>{val}</span>
              }
            </div>
          ))}
          {!profile.hasPaidMembership&&<div style={{marginTop:16}}><Link to="/membership" className="btn-primary" style={{display:"inline-block"}}>Complete Payment →</Link></div>}
        </div>
        <div className="dash-card">
          <div className="dash-card-title">Grant Access</div>
          <div style={{display:"flex",alignItems:"center",gap:14,padding:"14px",background:profile.hasPaidMembership?"rgba(34,197,94,0.05)":"rgba(239,68,68,0.05)",border:`1px solid ${profile.hasPaidMembership?"rgba(34,197,94,0.15)":"rgba(239,68,68,0.15)"}`,borderRadius:2}}>
            <div style={{fontSize:28}}>{profile.hasPaidMembership?"✅":"🔒"}</div>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:"var(--navy)",marginBottom:3}}>{profile.hasPaidMembership?"You can apply for grants":"Grant access locked"}</div>
              <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.6}}>{profile.hasPaidMembership?"Your membership is active. You are fully eligible to apply.":"Pay your ₦250 to unlock grant applications."}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── NOTIFICATIONS ─────────────────────────────────────────
  function NotificationsPanel() {
    return (
      <div style={{maxWidth:640}}>
        {notifications.length===0?(
          <div style={{textAlign:"center",padding:"60px",color:"var(--text-muted)"}}>
            <div style={{fontSize:36,marginBottom:12}}>🔔</div>
            <div style={{fontSize:15}}>No notifications yet.</div>
          </div>
        ):notifications.map((n,i)=>(
          <div key={n.id||i} style={{background:"white",border:"1px solid var(--border-light)",padding:"16px 20px",marginBottom:8,display:"flex",gap:12,alignItems:"flex-start",borderLeft:n.unread?"3px solid #C9963E":"3px solid transparent",cursor:n.unread?"pointer":"default"}} onClick={()=>n.unread&&handleMarkRead(n.id)}>
            <div style={{fontSize:18,flexShrink:0}}>{n.icon}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:13,color:"var(--navy)",lineHeight:1.6,fontWeight:n.unread?600:400}}>{n.text}</div>
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>{n.createdAt?.toDate?.().toLocaleDateString()||"Recently"}</div>
            </div>
            {n.unread&&<span className="badge badge-gold" style={{fontSize:10,flexShrink:0}}>NEW</span>}
          </div>
        ))}
      </div>
    );
  }

  // ── FUNDING ───────────────────────────────────────────────
  function FundingPanel() {
    return (
      <div style={{maxWidth:700}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3, 1fr)",gap:12,marginBottom:18}}>
          {[
            [`₦${grantBalance.toLocaleString()}`,"Total Received","All disbursed"],
            [`₦${pendingGrant.toLocaleString()}`,"Approved","Awaiting release"],
            [`₦${reviewGrant.toLocaleString()}`,"Under Review","Pending approval"],
          ].map(([v,l,s])=>(
            <div className="dash-stat" key={l}>
              <div className="dash-stat-val">{v}</div>
              <div className="dash-stat-label">{l}</div>
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:3}}>{s}</div>
            </div>
          ))}
        </div>
        <div className="dash-card">
          <div className="dash-card-title">Disbursement History</div>
          {applications.filter(a=>a.status==="Disbursed").length===0
            ?<div style={{textAlign:"center",padding:"24px",color:"var(--text-muted)",fontSize:14}}>No disbursements yet.</div>
            :applications.filter(a=>a.status==="Disbursed").map(a=>(
              <div key={a.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:"1px solid var(--border-light)"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--navy)"}}>{a.projectTitle}</div>
                  <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>Bank: {a.bankName||"—"} · Acct: {a.accountNumber||"—"}</div>
                  <div style={{fontSize:11,color:"var(--text-muted)"}}>{a.disbursedAt?.toDate?.().toLocaleDateString()||"—"}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,fontWeight:700,color:"#C9963E"}}>₦{(a.disbursedAmount||Number(a.fundingAmount)).toLocaleString()}</div>
                  <StatusBadge status={a.status}/>
                </div>
              </div>
            ))
          }
        </div>
      </div>
    );
  }

  // ── PROFILE ───────────────────────────────────────────────
  function ProfilePanel() {
    const [saving,setSaving]   = useState(false);
    const [saved,setSaved]     = useState(false);
    const [pForm,setPForm]     = useState({name:profile.name||"",phone:profile.phone||"",org:profile.org||"",state:profile.state||""});

    const handleSave = async () => {
      setSaving(true);
      const { updateMemberProfile } = await import("../firebase/services");
      await updateMemberProfile(currentUser.uid, pForm);
      await refreshProfile();
      setSaving(false); setSaved(true);
      setTimeout(()=>setSaved(false),3000);
    };

    return (
      <div style={{maxWidth:640}}>
        <div style={{background:"linear-gradient(135deg,#0B1F3A,#1a3a5c)",padding:"24px 28px",marginBottom:16}}>
          <div style={{width:52,height:52,borderRadius:"50%",background:"linear-gradient(135deg,#C9963E,#E8B96A)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:700,color:"#0B1F3A",marginBottom:12}}>
            {profile.name?.split(" ").map(n=>n[0]).join("").slice(0,2)||"RF"}
          </div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,color:"white",fontWeight:700}}>{profile.name}</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginTop:3}}>{currentUser?.email}</div>
          <div style={{marginTop:10,display:"inline-flex",alignItems:"center",gap:6,background:isVerified?"rgba(34,197,94,0.12)":"rgba(239,68,68,0.12)",padding:"4px 12px",borderRadius:50,fontSize:12}}>
            <span>{isVerified?"✅":"⚠️"}</span>
            <span style={{color:isVerified?"#4ade80":"#f87171",fontWeight:600}}>{isVerified?"Email Verified":"Email Not Verified"}</span>
            {!isVerified&&!verifySent&&<button onClick={handleResendVerification} style={{marginLeft:8,fontSize:11,color:"#f87171",background:"none",border:"1px solid rgba(239,68,68,0.3)",borderRadius:4,padding:"2px 8px",cursor:"pointer"}}>Resend</button>}
          </div>
        </div>
        <div className="dash-card">
          <div className="dash-card-title">Edit Profile</div>
          {saved&&<div className="success-box">✓ Profile saved successfully!</div>}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            {[["Full Name","name","text"],["Phone","phone","tel"],["Organization","org","text"],["State","state","text"]].map(([l,k,t])=>(
              <div className="form-group" key={k}>
                <label className="form-label">{l}</label>
                <input className="form-input" type={t} value={pForm[k]||""} onChange={e=>setPForm(f=>({...f,[k]:e.target.value}))} />
              </div>
            ))}
          </div>
          <div className="form-group" style={{marginTop:4}}>
            <label className="form-label">Email Address</label>
            <input className="form-input" value={currentUser?.email||""} disabled style={{background:"var(--off-white)",cursor:"not-allowed"}} />
          </div>
          <button className="btn-primary" style={{marginTop:14}} onClick={handleSave} disabled={saving}>
            {saving?"Saving...":"Save Changes"}
          </button>
        </div>
      </div>
    );
  }

  // ── SETTINGS ─────────────────────────────────────────────
  function SettingsPanel() {
    return (
      <div style={{maxWidth:600}}>
        <div className="dash-card" style={{marginBottom:16}}>
          <div className="dash-card-title">Account Settings</div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:"1px solid var(--border-light)"}}>
            <div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--navy)"}}>Email Verification</div>
              <div style={{fontSize:13,color:"var(--text-muted)",marginTop:2}}>{isVerified?"Your email is verified":"Your email is not verified yet"}</div>
            </div>
            {isVerified
              ?<span className="badge badge-green">✓ Verified</span>
              :<button onClick={handleResendVerification} className="btn-primary" style={{fontSize:13,padding:"8px 16px"}}>{verifySent?"Sent!":"Resend Verification"}</button>
            }
          </div>
          {verifyMsg&&<div style={{padding:"10px 0",fontSize:13,color:"var(--text-muted)"}}>{verifyMsg}</div>}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0",borderBottom:"1px solid var(--border-light)"}}>
            <div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--navy)"}}>Password</div>
              <div style={{fontSize:13,color:"var(--text-muted)",marginTop:2}}>Reset your account password via email</div>
            </div>
            <button onClick={async()=>{await auth.currentUser&&(await import("firebase/auth")).sendPasswordResetEmail(auth,currentUser.email);alert("Password reset email sent!");}} className="btn-outline-dark" style={{fontSize:13,padding:"8px 16px"}}>
              Reset Password
            </button>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 0"}}>
            <div>
              <div style={{fontSize:14,fontWeight:600,color:"var(--navy)"}}>Sign Out</div>
              <div style={{fontSize:13,color:"var(--text-muted)",marginTop:2}}>Sign out of your member account</div>
            </div>
            <button onClick={handleLogout} style={{fontSize:13,padding:"8px 16px",background:"rgba(239,68,68,0.08)",color:"#dc2626",border:"1px solid rgba(239,68,68,0.2)",borderRadius:4,cursor:"pointer"}}>
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }
}
